import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import cx from 'clsx';

import { AVAILABLE_L2_CHAIN_IDS } from '@/consts';
import {
  useCurrentAddressInfo,
  useSwitch,
  useUpdateCurrentAddressInfo,
  useCurrentAddressAccount,
  useCurrentL2KeyList,
  queryL2KeyList,
  invalidCurrentL2KeyList,
} from '@/hooks';
import { generateKey, register, devLog, encrypt } from '@/utils';
import { CHAINS_DICT } from '@/utils/chain';

import { PasswordInput } from '../PasswordInput';
import { SuccessHeader } from '../SuccessHeader';
import { ProcessSteps } from '../ProcessSteps';
import { AccountModal } from './AccountModal';
import style from './index.module.css';

const useRegisterAccount = (
  onClose: () => void,
  isResetAccount?: boolean,
  selectedChainId: string = 'eth',
) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmBtnLoading, setIsConfirmBtnLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const { address } = useCurrentAddressInfo();
  const [buttonText, setButtonText] = useState(t('AccountModal_AccountRegisterModal.send-request'));
  const [privateKey, setPrivateKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [customHeaderTitle, setCustomHeaderTitle] = useState('');
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const { data: accountData, ...accountQuery } = useCurrentAddressAccount();
  const { data: l2KeyListData, ...l2KeyListDataQuery } = useCurrentL2KeyList();
  const chainName = useMemo(() => CHAINS_DICT[selectedChainId]?.name || '', [selectedChainId]);
  const putAccountInfo = useUpdateCurrentAddressInfo();

  useEffect(() => {
    setIsButtonDisabled(!accountQuery.isFetched || !l2KeyListDataQuery.isFetched);
  }, [accountQuery.isFetched, l2KeyListDataQuery.isFetched]);

  const onPasswordChange = useCallback(() => {
    setIsButtonDisabled(passwordInputRef?.current?.value?.length !== 6);
  }, []);

  const processStepsConfig = useMemo(
    () => [
      {
        title: isResetAccount
          ? t('AccountModal_AccountRegisterModal.resetTitle')
          : t('AccountModal_AccountRegisterModal.generateTitle'),
        content: isResetAccount
          ? t('AccountModal_AccountRegisterModal.resetContent')
          : t('AccountModal_AccountRegisterModal.generateContent'),
      },
      {
        title: isResetAccount
          ? t('AccountModal_AccountRegisterModal.resetStep2Title')
          : t('AccountModal_AccountRegisterModal.registStep2Title'),
        content: isResetAccount
          ? t('AccountModal_AccountRegisterModal.resetStep2Content', { chainName })
          : t('AccountModal_AccountRegisterModal.registStep2Content', { chainName }),
      },
    ],
    [chainName, isResetAccount, t],
  );

  const startRegister = useCallback(
    (next, pk) => {
      if (!address) {
        return;
      }
      register(pk, address, selectedChainId)
        .then(
          () => {
            const pollingQueryRegistrationNonce = () => {
              queryL2KeyList(address).then(({ primaryL2Key }) => {
                if (primaryL2Key?.public_key === pk) {
                  window.clearInterval(timer);
                  setIsButtonDisabled(true);
                  setIsLoading(false);
                  setButtonText(t('AccountModal_AccountRegisterModal.confirm'));
                  setCustomHeaderTitle(t('AccountModal_AccountRegisterModal.set-password'));
                  invalidCurrentL2KeyList();
                  next();
                }
              });
            };
            const timer = window.setInterval(pollingQueryRegistrationNonce, 2500);
          },
          () => {
            setIsLoading(false);
          },
        )
        .catch(e => {
          devLog('Contract Registration', e);
          setIsLoading(false);
        });
    },
    [address, selectedChainId, t],
  );

  const steps = [
    {
      name: isResetAccount
        ? t('AccountModal_AccountRegisterModal.resetKeyStep')
        : t('AccountModal_AccountRegisterModal.generate-debankkey'),
      handler: async (next: () => void) => {
        if (!address) {
          onClose();
          return;
        }
        setIsLoading(true);
        setButtonText(t('AccountModal_AccountRegisterModal.try-again'));
        if (stepIndex === 0) {
          const nextNonce =
            (l2KeyListData?.list.find(item => item.chain_id === selectedChainId)?.nonce || 0) + 1;

          try {
            const { privateKey, publicKey: pk } = await generateKey(
              address,
              nextNonce,
              selectedChainId,
            );
            setPrivateKey(privateKey);
            setPublicKey(pk);
            setStepIndex(1);

            // auto start next step
            startRegister(next, pk);
          } catch (error) {
            setIsLoading(false);
            Sentry.captureException(error);
          }
        } else if (stepIndex === 1) {
          startRegister(next, publicKey);
        }
      },
      textWidth: 64,
      bottomText:
        stepIndex === 1 && isLoading ? t('AccountModal_AccountRegisterModal.registTip') : '',
      content: (
        <ProcessSteps
          steps={processStepsConfig}
          activeStepIndex={stepIndex}
          isLoading={isLoading}
        />
      ),
    },
    {
      name: t('AccountModal_AccountRegisterModal.setPwdStep'),
      handler: (next: () => void) => {
        const password = passwordInputRef?.current?.value;
        if (!password || password.length !== 6) {
          return;
        }
        try {
          const encryptedPrivateKey = encrypt(privateKey, password);
          if (address) {
            setIsConfirmBtnLoading(true);
            putAccountInfo({
              account: {
                publicKey,
                encryptedPrivateKey,
                chainId: selectedChainId,
              },
            });

            // make sure that the account info is saved to local storage
            setTimeout(() => {
              setIsConfirmBtnLoading(false);
              onClose();
            }, 1500);
          }
        } catch (e) {
          Sentry.captureException(e);
        }
      },
      textWidth: 114,
      content: (
        <div className={style.headroomBody}>
          <SuccessHeader
            text={
              isResetAccount
                ? t('AccountModal_AccountRegisterModal.reset-successfully')
                : t('AccountModal_AccountRegisterModal.registed')
            }></SuccessHeader>
          <div className={style.passwordTitle}>
            {isResetAccount
              ? t('AccountModal_AccountRegisterModal.setnew')
              : t('AccountModal_AccountRegisterModal.resetPwd')}
          </div>
          <PasswordInput
            className={style.registerPassword}
            passwordInputRef={passwordInputRef}
            onPasswordChange={onPasswordChange}
            showInputTips
          />
        </div>
      ),
    },
  ];

  return {
    steps,
    isLoading: isConfirmBtnLoading,
    // successText,
    customHeaderTitle,
    buttonText,
    isButtonDisabled,
  };
};
export const AccountRegisterModal = ({
  turnOff,
  isResetAccount,
}: {
  isResetAccount?: boolean;
  turnOff(): void;
}) => {
  const { t } = useTranslation();

  const [selectedChainId, setSelectedChainId] = useState('eth');

  const chains = useMemo(() => {
    const sortedChains = AVAILABLE_L2_CHAIN_IDS.map(x => CHAINS_DICT[x]);
    return sortedChains;
  }, []);

  const { steps, buttonText, isButtonDisabled, customHeaderTitle, isLoading } = useRegisterAccount(
    turnOff,
    isResetAccount,
    selectedChainId,
  );
  const { on: onSwitchCHainModal, turnOff: turnOffSwitchCHainModal } = useSwitch(true);

  const selectChainStep = useMemo(
    () => [
      {
        handler: () => {
          turnOffSwitchCHainModal();
        },
        textWidth: 64,
        content: (
          <div className={cx(style.selectChainContainer, style.headroomBody)}>
            <div className={style.title}>{t('AccountModal_AccountRegisterModal.zboz82')}</div>
            <div className={style.description}>
              {t('AccountModal_AccountRegisterModal.1wp64ty')}
            </div>

            <div className={style.chainList}>
              {chains.map(chain =>
                chain ? (
                  <div
                    key={chain.serverId}
                    onClick={() => setSelectedChainId(chain.serverId)}
                    className={cx(
                      style.chainItem,
                      chain.serverId === selectedChainId && style.isSelected,
                    )}>
                    <img className={style.chainIcon} src={(chain as any).logo} alt="" />
                    {chain.name}
                  </div>
                ) : null,
              )}
            </div>
          </div>
        ),
      },
    ],
    [chains, selectedChainId, turnOffSwitchCHainModal, t],
  );

  const headerTitle = useMemo(
    () =>
      customHeaderTitle
        ? customHeaderTitle
        : isResetAccount
          ? t('AccountModal_AccountRegisterModal.reset-key')
          : t('AccountModal_AccountRegisterModal.register'),
    [customHeaderTitle, isResetAccount, t],
  );

  return onSwitchCHainModal ? (
    <AccountModal
      isLoading={false}
      hideSteps
      headerTitle={headerTitle}
      turnOff={turnOff}
      steps={selectChainStep}
      buttonText={t('AccountModal_AccountRegisterModal.next')}
    />
  ) : (
    <AccountModal
      headerTitle={headerTitle}
      turnOff={turnOff}
      steps={steps}
      isLoading={isLoading}
      buttonText={buttonText}
      isButtonDisabled={isButtonDisabled}
    />
  );
};
